import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  ListItemIcon,
  Collapse,
  FormControlLabel,
  Switch,
  Grid,
  Menu,
} from '@mui/material';
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  StarBorder as StarBorderIcon,
  Star as StarIcon,
  Repeat as RepeatIcon,
  CalendarToday as CalendarTodayIcon,
  CalendarMonth as CalendarMonthIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../api';
import CategorySidebar from '../components/CategorySidebar';
import TaskListItem from '../components/TaskListItem';
import QuickAddForm from '../components/QuickAddForm';
import * as MuiIcons from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useSidebar } from '../context/SidebarContext';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { playCompletionSound } from '../utils/completionSounds';
import { useNavigate } from 'react-router-dom';

const TaskList = () => {
  const { user, loading: authLoading } = useAuth();
  const { hasActiveSubscription, loading: subscriptionLoading } = useSubscription();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    priority: 'medium',
    category: '',
    recurring: {
      enabled: false,
      frequency: 'daily',
      customValue: 1,
      customUnit: 'days',
      weeklyDays: [] // Array of selected days for weekly recurrence
    }
  });
  const [sortBy, setSortBy] = useState('dueDate');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [customOrder, setCustomOrder] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [dropPosition, setDropPosition] = useState(null); // 'above' or 'below' - setter used but value not read (for future use)
  const [showDropGap, setShowDropGap] = useState(null); // { index: number, position: 'above' | 'below' }
  const [draggedTask, setDraggedTask] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [quickDateValue, setQuickDateValue] = useState('');
  const [isUpdatingDate, setIsUpdatingDate] = useState(false);
  const [completedTasks, setCompletedTasks] = useState({});
  
  // Refs to prevent infinite loops
  const isFetchingRef = useRef(false);
  const hasInitialLoadRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const latestTasksRequestRef = useRef(0);

  const { sidebarCollapsed, toggleSidebar } = useSidebar();
  const location = useLocation();
  const theme = useTheme();

  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const [allTasks, setAllTasks] = useState([]); // Store all tasks for counting
  
  // Pagination state for active tasks
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [totalTasks, setTotalTasks] = useState(0); // Setter used but value not read (for future use)
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Pagination state for completed tasks
  const [completedCurrentPage, setCompletedCurrentPage] = useState(1);
  const [completedHasMore, setCompletedHasMore] = useState(true);
  const [completedTasksLoaded, setCompletedTasksLoaded] = useState(false);
  const [isLoadingMoreCompleted, setIsLoadingMoreCompleted] = useState(false);

  const fetchAllTasks = useCallback(async (skipLoading = false) => {
    // Prevent concurrent fetches - if already fetching, return immediately
    // The caller should handle the case where data might be stale
    if (isFetchingRef.current) {
      return [];
    }
    
    try {
      isFetchingRef.current = true;
      if (!skipLoading) {
        setLoading(true); // Only set loading on initial fetch, not on refreshes
      }
      const token = localStorage.getItem('token');
      const response = await api.get('/api/tasks', {
        headers: { Authorization: `Bearer ${token}` }
        // No pagination params - get all tasks by default
      });
      // Response is always an array when pagination is not used
      const tasks = Array.isArray(response.data) 
        ? response.data 
        : (response.data.tasks || []);
      setAllTasks(tasks);
      if (!skipLoading) {
        setLoading(false); // Set loading to false when done
      }
      return tasks;
    } catch (error) {
      console.error('Error fetching all tasks:', error);
      if (!skipLoading) {
        setLoading(false); // Set loading to false even on error
      }
      return [];
    } finally {
      isFetchingRef.current = false;
    }
  }, []); // No dependencies - ref prevents concurrent calls

  const fetchTasks = useCallback(async (categoryFilter = null, focusFilter = false, page = 1, append = false, sortParam = null, skipLoading = false) => {
    // Prevent concurrent fetches
    if (isFetchingRef.current && focusFilter) {
      return;
    }

    const requestId = ++latestTasksRequestRef.current;
    
    try {
      if (focusFilter) {
        isFetchingRef.current = true;
      }
      if (append) {
        setIsLoadingMore(true);
        isLoadingMoreRef.current = true;
      } else if (page === 1 && !skipLoading) {
        // Set loading for initial load or first page (but skip for category filter changes)
        setLoading(true);
      }
      const token = localStorage.getItem('token');
      let url = `/api/tasks`;
      const params = [];
      // When focus mode is active, we need server-side filtering (for date calculations)
      if (focusFilter) {
        params.push('focus=true');
        if (categoryFilter) params.push(`category=${categoryFilter}`);
      }
      // When focus mode is NOT active, we'll filter client-side for instant response
      if (params.length) url += '?' + params.join('&');
      
      // Always use pagination for performance with large datasets
      // Don't include descriptions in list view for better performance
      const paramsObj = {
        paginate: 'true',
        page: page,
        limit: 50,
        status: 'pending' // Only fetch pending tasks for active list
        // Note: descriptions excluded for performance - fetched on-demand when opening task
      };
      // Send browser timezone when focus filter is active to ensure server uses same timezone
      if (focusFilter) {
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        paramsObj.timezone = browserTimezone;
      }
      // Pass sort parameter to server (except priority which needs client-side sorting)
      const sortToUse = sortParam !== null ? sortParam : sortBy;
      if (sortToUse && sortToUse !== 'priority') {
        paramsObj.sortBy = sortToUse;
      }
      if (categoryFilter && !focusFilter) {
        paramsObj.category = categoryFilter;
      }
      if (debouncedSearchQuery && debouncedSearchQuery.trim() !== '') {
        paramsObj.search = debouncedSearchQuery.trim();
      }
      
      const response = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params: paramsObj
      });

      // Do not let an older list snapshot replace tasks added by a mutation
      // or returned by a newer request.
      if (requestId !== latestTasksRequestRef.current) return;
      
      // Handle paginated response
      if (response.data.pagination) {
        const tasks = response.data.tasks || [];
        console.log('Fetched tasks (paginated):', tasks.length, 'tasks');
        if (append) {
          setTasks(prevTasks => [...prevTasks, ...tasks]);
        } else {
          setTasks(tasks);
        }
        setHasMore(response.data.pagination.hasMore);
        setTotalTasks(response.data.pagination.total);
        setCurrentPage(page);
      } else {
        // Fallback for non-paginated response
        const tasks = Array.isArray(response.data) ? response.data : [];
        console.log('Fetched tasks (non-paginated):', tasks.length, 'tasks');
        setTasks(append ? prevTasks => [...prevTasks, ...tasks] : tasks);
        // If no pagination info, assume we have all tasks
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      // Set empty array on error to prevent blank page
      if (!append && requestId === latestTasksRequestRef.current) {
        setTasks([]);
      }
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
      if (focusFilter) {
        isFetchingRef.current = false;
      }
    }
  }, [sortBy, debouncedSearchQuery]);

  // Fetch task counts from optimized API endpoint (uses aggregation)
  const [taskCounts, setTaskCounts] = useState({
    all: { open: 0, completed: 0 },
    focus: { open: 0, completed: 0 },
    categories: {}
  });

  const fetchTaskCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      // Detect browser timezone to ensure count matches client-side filtering
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await api.get('/api/tasks/counts', {
        headers: { Authorization: `Bearer ${token}` },
        params: { timezone: browserTimezone }
      });
      setTaskCounts(response.data);
    } catch (error) {
      console.error('Error fetching task counts:', error);
    }
  }, []);

  // Helper to refresh tasks using current filter state - optimized for performance
  const refreshTasks = useCallback(async () => {
    // Reset pagination and fetch first page
    setCurrentPage(1);
    setHasMore(true);
    await fetchTasks(selectedCategory, focusMode, 1, false);
    // Refresh counts after mutations
    fetchTaskCounts();
  }, [focusMode, selectedCategory, fetchTasks, fetchTaskCounts]);
  
  // Load more tasks for infinite scroll
  const loadMoreTasks = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (!hasMore || isLoadingMore || isFetchingRef.current || isLoadingMoreRef.current) {
      return;
    }
    
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    
    try {
      const nextPage = currentPage + 1;
      await fetchTasks(selectedCategory, focusMode, nextPage, true);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, currentPage, selectedCategory, focusMode, fetchTasks]);

  // updateFocusForToday - deprecated endpoint, kept for reference but not used
  // const updateFocusForToday = useCallback(async () => {
  //   try {
  //     const token = localStorage.getItem('token');
  //     await api.post('/api/tasks/update-focus-today', {}, {
  //       headers: { Authorization: `Bearer ${token}` }
  //     });
  //   } catch (error) {
  //     console.error('Error updating focus for today:', error);
  //   }
  // }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Debounce search query to improve performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);



  // Fetch counts on mount and when user/auth changes
  useEffect(() => {
    if (user && !authLoading) {
      fetchTaskCounts();
    }
  }, [user, authLoading, fetchTaskCounts]);

  useEffect(() => {
    if (user && !authLoading && !hasInitialLoadRef.current) {
      // Initial load - fetch first page of tasks
      hasInitialLoadRef.current = true;
      setCurrentPage(1);
      setHasMore(true);
      setCustomOrder([]);
      setSortBy('dueDate');
      // Fetch with default sort
      fetchTasks(null, false, 1, false, 'dueDate');
    }
  }, [user, authLoading, fetchTasks]);

  // Handle filter and sort changes - reset pagination and fetch first page
  useEffect(() => {
    if (!user || authLoading || !hasInitialLoadRef.current) {
      return;
    }
    
    // Reset pagination when filters or sort change
    setCurrentPage(1);
    setHasMore(true);
    // Skip loading indicator for category/filter changes to avoid page refresh feel
    fetchTasks(selectedCategory, focusMode, 1, false, sortBy, true);
  }, [selectedCategory, focusMode, sortBy, debouncedSearchQuery, user, authLoading, fetchTasks]);

  // Refresh tasks when navigating back from task detail
  useEffect(() => {
    if (location.pathname === '/tasks' && user && !authLoading) {
      // Don't call updateFocusForToday here - it would re-focus manually unfocused tasks
      // Only refresh if we're actually navigating to this page (not on initial load)
      const isInitialLoad = !allTasks || allTasks.length === 0;
      if (!isInitialLoad) {
        fetchAllTasks().then(() => {
          // Filtering will be handled by the category/focus filter effect
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user, authLoading]); // allTasks and fetchAllTasks intentionally omitted to prevent loops

  const handleOpenDialog = useCallback(async (task = null) => {
    if (task) {
      // If task doesn't have description, fetch full task details
      let fullTask = task;
      if (!task.description && task._id) {
        try {
          const token = localStorage.getItem('token');
          const response = await api.get(`/api/tasks/${task._id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          fullTask = response.data;
        } catch (error) {
          console.error('Error fetching full task details:', error);
          // Use the task we have even if it doesn't have description
        }
      }
      
      setEditingTask(fullTask);
      setFormData({
        title: fullTask.title,
        description: fullTask.description || '',
        dueDate: normalizeDate(fullTask.dueDate),
        priority: fullTask.priority,
        category: fullTask.category || '',
        recurring: {
          enabled: fullTask.recurring?.enabled || false,
          frequency: fullTask.recurring?.frequency || 'daily',
          customValue: fullTask.recurring?.customValue || 1,
          customUnit: fullTask.recurring?.customUnit || 'days',
          weeklyDays: fullTask.recurring?.weeklyDays || []
        }
      });
    } else {
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        dueDate: '',
        priority: 'medium',
        category: selectedCategory || '', // Pre-populate with selected category if any
        recurring: {
          enabled: false,
          frequency: 'daily',
          customValue: 1,
          customUnit: 'days',
          weeklyDays: []
        }
      });
    }
    setOpenDialog(true);
  }, [selectedCategory]);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setEditingTask(null);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only block when subscription status has loaded and user has no access (avoid false "expired" while loading)
    if (!subscriptionLoading && !hasActiveSubscription) {
      alert('Your subscription has expired. Please subscribe to continue creating and editing tasks.');
      navigate('/app/subscription');
      return;
    }
    
    // Validate weekly recurrence
    if (formData.recurring.enabled && formData.recurring.frequency === 'weekly') {
      if (!formData.recurring.weeklyDays || formData.recurring.weeklyDays.length === 0) {
        alert('Please select at least one day for weekly recurrence');
        return;
      }
    }
    
    try {
      const token = localStorage.getItem('token');
      console.log('=== SUBMITTING TASK ===');
      console.log('Form data recurring:', formData.recurring);
      console.log('Weekly days:', formData.recurring.weeklyDays);
      const submitData = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        category: formData.category || null,
        recurring: formData.recurring,
      };
      console.log('Submit data:', submitData);
      
      // Handle dueDate consistently
      if (editingTask) {
        // For editing, compare dates properly by extracting just the date part
        const originalDueDate = normalizeDate(editingTask.dueDate);
        const newDueDate = formData.dueDate || '';
        
        if (newDueDate !== originalDueDate) {
          if (newDueDate) {
            submitData.dueDate = newDueDate;
          } else {
            submitData.dueDate = null;
          }
        }
      } else {
        // For new tasks, include dueDate if provided
        if (formData.dueDate) {
          submitData.dueDate = formData.dueDate;
        }
      }
      
      let response;
      if (editingTask) {
        response =         await api.put(
          `/api/tasks/${editingTask._id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Update response:', response.data);
        console.log('Updated recurring:', response.data.recurring);
        
        // Optimistically update the task in the list
        const updatedTask = response.data;
        setTasks(prevTasks => prevTasks.map(task => 
          task._id === updatedTask._id ? updatedTask : task
        ));
        setAllTasks(prevAllTasks => prevAllTasks.map(task => 
          task._id === updatedTask._id ? updatedTask : task
        ));
        // Also update in completed list if it's there
        setCompletedTasksList(prevCompleted => prevCompleted.map(task => 
          task._id === updatedTask._id ? updatedTask : task
        ));
      } else {
        response = await api.post(
          `/api/tasks`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Create response:', response.data);
        
        // Optimistically add the new task to the list without full refresh
        const newTask = response.data;
        
        // Only add to active tasks if it's pending and matches current filters
        if (newTask.status === 'pending') {
          // Check if task matches current filters
          const matchesCategory = !selectedCategory || newTask.category === selectedCategory;
          const matchesFocus = !focusMode || (newTask.focus === true && !newTask.manuallyUnfocused);
          
          if (matchesCategory && matchesFocus) {
            // Add to tasks list - insert at appropriate position based on sort
            setTasks(prevTasks => {
              const newTasks = [...prevTasks, newTask];
              // Re-sort based on current sortBy
              if (sortBy === 'dueDate') {
                return newTasks.sort((a, b) => {
                  if (!a.dueDate) return 1;
                  if (!b.dueDate) return -1;
                  return new Date(a.dueDate) - new Date(b.dueDate);
                });
              } else if (sortBy === 'priority') {
                const priorityOrder = { high: 0, medium: 1, low: 2 };
                return newTasks.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
              } else if (sortBy === 'createdAt') {
                return newTasks.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
              }
              return newTasks;
            });
          }
        }
        
        // Always add to allTasks for counts
        setAllTasks(prevAllTasks => [...prevAllTasks, newTask]);
      }
      fetchTaskCounts(); // Refresh counts after create/update
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving task:', error);
      if (error.response?.status === 403) {
        alert('Your subscription has expired. Please subscribe to continue creating and editing tasks.');
        navigate('/app/subscription');
      }
    }
  };

  const handleQuickAdd = useCallback(async (payload) => {
    if (!subscriptionLoading && !hasActiveSubscription) {
      alert('Your subscription has expired. Please subscribe to continue creating and editing tasks.');
      navigate('/app/subscription');
      throw new Error('Subscription required');
    }
    const token = localStorage.getItem('token');
    const submitData = {
      ...payload,
      dueDate: payload.dueDate || undefined,
    };
    let response;
    try {
      response = await api.post('/api/tasks', submitData, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      if (error.response?.status === 403) {
        alert('Your subscription has expired. Please subscribe to continue creating and editing tasks.');
        navigate('/app/subscription');
      }
      throw error;
    }
    const newTask = response.data;
    if (newTask.status === 'pending') {
      const matchesCategory = !selectedCategory || newTask.category === selectedCategory;
      const matchesFocus = !focusMode || (newTask.focus === true && !newTask.manuallyUnfocused);
      if (matchesCategory && matchesFocus) {
        setTasks((prevTasks) => {
          const newTasks = [...prevTasks, newTask];
          if (sortBy === 'dueDate') {
            return newTasks.sort((a, b) => {
              if (!a.dueDate) return 1;
              if (!b.dueDate) return -1;
              return new Date(a.dueDate) - new Date(b.dueDate);
            });
          }
          if (sortBy === 'priority') {
            const priorityOrder = { high: 0, medium: 1, low: 2 };
            return newTasks.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
          }
          if (sortBy === 'createdAt') {
            return newTasks.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          }
          return newTasks;
        });
      }
    }
    setAllTasks((prevAllTasks) => [...prevAllTasks, newTask]);
    fetchTaskCounts();
  }, [subscriptionLoading, hasActiveSubscription, navigate, selectedCategory, focusMode, sortBy, fetchTaskCounts]);

  const handleDelete = async (taskId) => {
    // Only block when subscription status has loaded and user has no access
    if (!subscriptionLoading && !hasActiveSubscription) {
      alert('Your subscription has expired. Please subscribe to continue deleting tasks.');
      navigate('/app/subscription');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }
    
    // Optimistic update - remove from UI immediately
    const taskToDelete = tasks.find(t => t._id === taskId);
    setTasks(prevTasks => prevTasks.filter(task => task._id !== taskId));
    setAllTasks(prevAllTasks => prevAllTasks.filter(task => task._id !== taskId));
    
    // Perform API call in background (non-blocking)
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Success - optimistic update was correct, refresh counts
      fetchTaskCounts();
    } catch (error) {
      console.error('Error deleting task:', error);
      if (error.response?.status === 403) {
        alert('Your subscription has expired. Please subscribe to continue deleting tasks.');
        navigate('/app/subscription');
        return;
      }
      // Revert optimistic update on error
      if (taskToDelete) {
        setTasks(prevTasks => [...prevTasks, taskToDelete].sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        ));
        setAllTasks(prevAllTasks => [...prevAllTasks, taskToDelete].sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        ));
      }
      // Refresh to get correct state
      refreshTasks();
    }
  };

  const sortTaskList = useCallback((taskList) => {
    return [...taskList].sort((a, b) => {
      if (sortBy === 'dueDate') {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate) - new Date(b.dueDate);
      }
      if (sortBy === 'priority') {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      if (sortBy === 'createdAt') {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      return 0;
    });
  }, [sortBy]);

  const upsertTaskInList = useCallback((taskList, taskToUpsert) => {
    if (!taskToUpsert?._id) return taskList;
    const withoutDuplicate = taskList.filter(task => task._id !== taskToUpsert._id);
    return sortTaskList([...withoutDuplicate, taskToUpsert]);
  }, [sortTaskList]);

  const taskMatchesCurrentList = useCallback((task) => {
    if (!task || task.status === 'completed') return false;
    if (focusMode && !(task.focus === true && !task.manuallyUnfocused)) return false;
    if (selectedCategory && String(task.category || '') !== String(selectedCategory)) return false;
    return true;
  }, [focusMode, selectedCategory]);

  const addNextRecurringTaskToState = useCallback((nextTask) => {
    if (!nextTask?._id) return;

    setAllTasks(prevAllTasks => upsertTaskInList(prevAllTasks, nextTask));

    if (taskMatchesCurrentList(nextTask)) {
      setTasks(prevTasks => upsertTaskInList(prevTasks, nextTask));
    }
  }, [taskMatchesCurrentList, upsertTaskInList]);

  const completeRecurringTask = async (task, retryCount = 0) => {
    const maxRetries = 1;
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(
        `/api/tasks/${task._id}/complete-recurring`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      if (retryCount < maxRetries && error?.response?.status !== 400 && error?.response?.status !== 403) {
        await new Promise(r => setTimeout(r, 2000));
        return completeRecurringTask(task, retryCount + 1);
      }
      throw error;
    }
  };



  const handleToggleStatus = useCallback(async (taskId, currentStatus, event) => {
    // Prevent any default behavior that might cause page refresh
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Only block when subscription status has loaded and user has no access
    if (currentStatus === 'pending' && !subscriptionLoading && !hasActiveSubscription) {
      alert('Your subscription has expired. Please subscribe to continue completing tasks.');
      navigate('/app/subscription');
      return;
    }
    
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    
    // Optimistic update - update UI immediately for instant feedback
    // Store the original task for potential rollback
    const originalTask = tasks.find(t => t._id === taskId) 
      || completedTasksList.find(t => t._id === taskId)
      || allTasks.find(t => t._id === taskId);
    const isRecurringCompletion = newStatus === 'completed' && originalTask?.recurring?.enabled;

    // Invalidate list snapshots that started before this mutation.
    latestTasksRequestRef.current += 1;
    
    // Update allTasks first (for counts and potential uncomplete)
    setAllTasks(prevAllTasks => prevAllTasks.map(task => 
      task._id === taskId ? { ...task, status: newStatus } : task
    ));
    
    // Update tasks list - remove completed tasks immediately
    if (newStatus === 'completed') {
      // Remove from active tasks list immediately
      setTasks(prevTasks => prevTasks.filter(task => task._id !== taskId));
    } else {
      // If uncompleting, update status in place (task should already be in list if visible)
      setTasks(prevTasks => {
        const taskExists = prevTasks.some(t => t._id === taskId);
        if (taskExists) {
          // Task already in list, just update status
          return prevTasks.map(t => t._id === taskId ? { ...t, status: newStatus } : t);
        } else if (originalTask) {
          // Task was completed, add it back with new status
          return [...prevTasks, { ...originalTask, status: newStatus }];
        }
        return prevTasks;
      });
      
      // Remove from completed tasks list immediately when uncompleting
      setCompletedTasksList(prevCompleted => prevCompleted.filter(task => task._id !== taskId));
    }
    
    // Play completion sound and animation if completing (non-blocking)
    if (newStatus === 'completed') {
      const selectedSound = localStorage.getItem('completion_sound') || 'chord';
      playCompletionSound(selectedSound);
      setCompletedTasks(prev => ({ ...prev, [taskId]: Date.now() }));
      
      // If completed section is open, add task to completed list immediately
      if (showCompleted && originalTask) {
        const completedTask = { ...originalTask, status: newStatus, updatedAt: new Date().toISOString() };
        setCompletedTasksList(prevCompleted => {
          // Check if task already exists (avoid duplicates)
          if (prevCompleted.some(t => t._id === taskId)) {
            return prevCompleted.map(t => t._id === taskId ? completedTask : t);
          }
          // Add to beginning of list (most recently completed first)
          return [completedTask, ...prevCompleted];
        });
      }
    }
    
    // Perform API call in background (non-blocking)
    try {
      const token = localStorage.getItem('token');
      const response = isRecurringCompletion
        ? await completeRecurringTask(originalTask)
        : await api.put(
          `/api/tasks/${taskId}`,
          { status: newStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );

      // Ignore list snapshots that started while the mutation was running.
      latestTasksRequestRef.current += 1;
      const responseTask = isRecurringCompletion
        ? (response.completedTask || { ...originalTask, status: 'completed' })
        : response.data;
      const nextRecurringTask = isRecurringCompletion ? response.nextTask : null;
      
      // Update with server response to ensure consistency
      // Ensure the response has the correct status (defensive check)
      const updatedTask = responseTask && responseTask.status === newStatus
        ? responseTask
        : { ...responseTask, status: newStatus };
      
      // Update allTasks with server response
      setAllTasks(prevAllTasks => prevAllTasks.map(task => 
        task._id === taskId ? updatedTask : task
      ));
      
      // Update tasks list based on status
      if (newStatus === 'completed') {
        // Ensure completed task is removed from active list
        setTasks(prevTasks => prevTasks.filter(task => task._id !== taskId));
        
        // If completed section is open, ensure task is in completed list with server data
        if (showCompleted) {
          setCompletedTasksList(prevCompleted => {
            // Check if task already exists
            if (prevCompleted.some(t => t._id === taskId)) {
              return prevCompleted.map(t => t._id === taskId ? updatedTask : t);
            }
            // Add to beginning of list (most recently completed first)
            return [updatedTask, ...prevCompleted];
          });
        }
      } else {
        // Task is being uncompleted, add/update it in the active list
        setTasks(prevTasks => {
          const taskExists = prevTasks.some(t => t._id === taskId);
          if (taskExists) {
            // Update existing task
            return prevTasks.map(t => t._id === taskId ? updatedTask : t);
          } else {
            // Add task back to list
            return [...prevTasks, updatedTask];
          }
        });
        
        // Remove from completed tasks list immediately when uncompleting
        setCompletedTasksList(prevCompleted => prevCompleted.filter(task => task._id !== taskId));
      }
      
      // Refresh counts after status change
      fetchTaskCounts();

      if (nextRecurringTask) {
        addNextRecurringTaskToState(nextRecurringTask);
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      if (error.response?.status === 403) {
        alert('Your subscription has expired. Please subscribe to continue completing tasks.');
        navigate('/app/subscription');
        return;
      }
      // Revert optimistic update on error
      setTasks(prevTasks => prevTasks.map(task => 
        task._id === taskId ? { ...task, status: currentStatus } : task
      ));
      setAllTasks(prevAllTasks => prevAllTasks.map(task => 
        task._id === taskId ? { ...task, status: currentStatus } : task
      ));
      // Refresh to get correct state
      refreshTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, refreshTasks, fetchAllTasks, fetchTasks, focusMode, selectedCategory, fetchTaskCounts, sortBy, subscriptionLoading, hasActiveSubscription, navigate, addNextRecurringTaskToState]); // allTasks, completedTasksList, showCompleted intentionally omitted to prevent loops


  // getPriorityColor - unused, removed to fix linting
  // const getPriorityColor = (priority) => {
  //   switch (priority) {
  //     case 'high':
  //       return 'error';
  //     case 'medium':
  //       return 'warning';
  //     case 'low':
  //       return 'success';
  //     default:
  //       return 'default';
  //   }
  // };

  const getCategoryName = useCallback((categoryId) => {
    if (!categoryId) return null;
    const category = categories.find(cat => cat._id === categoryId);
    return category ? category.name : null;
  }, [categories]);

  const getCategoryIcon = useCallback((categoryId) => {
    if (!categoryId) return null;
    const category = categories.find(cat => cat._id === categoryId);
    if (!category) return null;
    const IconComponent = MuiIcons[category.icon];
    return IconComponent || MuiIcons.Label;
  }, [categories]);

  // Memoize today's date calculation to avoid recalculating on every render
  const todayDateInfo = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return { today, tomorrow };
  }, []); // Only recalculate once per day (could add date dependency for midnight updates)

  // Filter tasks by search query (memoized)
  // Also filter out manually unfocused tasks when in Focus mode
  const filteredTasks = useMemo(() => {
    if (!Array.isArray(tasks) || tasks.length === 0) {
      return [];
    }
    
    let filtered = tasks;
    
    // If in Focus mode, only show manually focused tasks
    // Focus is now manual-only - users must explicitly set tasks to focus
    if (focusMode) {
      filtered = tasks.filter(task => {
        // Exclude manually unfocused tasks from Focus view
        if (task.manuallyUnfocused) {
          return false;
        }
        
        // Only include tasks that are explicitly focused
        return task.focus === true;
      });
    }
    
    // Apply search filter if search query exists (use debounced query)
    if (debouncedSearchQuery && debouncedSearchQuery.trim() !== '') {
    const query = debouncedSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(task => {
      // Search in title
      if (task.title && task.title.toLowerCase().includes(query)) {
        return true;
      }
      // Search in description
      if (task.description && task.description.toLowerCase().includes(query)) {
        return true;
      }
      // Search in category name
      if (task.category) {
        const categoryName = getCategoryName(task.category);
        if (categoryName && categoryName.toLowerCase().includes(query)) {
          return true;
        }
      }
      return false;
      });
    }
    
    return filtered;
  }, [tasks, debouncedSearchQuery, focusMode, getCategoryName]);

  // Sorting logic - separate active and completed tasks (memoized)
  const activeTasks = useMemo(() => 
    Array.isArray(filteredTasks) ? filteredTasks.filter(task => task.status !== 'completed') : [], 
    [filteredTasks]
  );

  // Sort only active tasks (memoized)
  const sortedActiveTasks = useMemo(() => {
    if (sortBy === 'custom' && customOrder.length > 0) {
      // Use custom order if available
      return customOrder;
    } else {
      // Use regular sorting
      return [...activeTasks].sort((a, b) => {
        if (sortBy === 'dueDate') {
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(a.dueDate) - new Date(b.dueDate);
        } else if (sortBy === 'priority') {
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        } else if (sortBy === 'createdAt') {
          return new Date(b.createdAt) - new Date(a.createdAt);
        }
        return 0;
      });
    }
  }, [activeTasks, sortBy, customOrder]);


  // Lazy load completed tasks - only fetch when showCompleted is true
  const [completedTasksList, setCompletedTasksList] = useState([]);
  
  const fetchCompletedTasks = useCallback(async (page = 1, append = false) => {
    try {
      const token = localStorage.getItem('token');
      const params = {
        paginate: 'true',
        page: page,
        limit: 50,
        status: 'completed'
        // Note: descriptions excluded for performance - fetched on-demand when opening task
      };
    
      // Include category filter if one is selected
      if (selectedCategory) {
        params.category = selectedCategory;
      }
    
      if (focusMode) {
        params.focus = 'true';
        // Detect browser timezone to ensure list matches client-side filtering and count
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        params.timezone = browserTimezone;
      }
      
      const response = await api.get('/api/tasks', {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });
      
      if (response.data.pagination) {
        const tasks = response.data.tasks || [];
        if (append) {
          setCompletedTasksList(prev => [...prev, ...tasks]);
        } else {
          setCompletedTasksList(tasks);
        }
        setCompletedHasMore(response.data.pagination.hasMore);
        setCompletedCurrentPage(page);
      } else {
        const tasks = Array.isArray(response.data) ? response.data : [];
        setCompletedTasksList(append ? prev => [...prev, ...tasks] : tasks);
      }
    } catch (error) {
      console.error('Error fetching completed tasks:', error);
    }
  }, [focusMode, selectedCategory]);
  
  // Load more completed tasks
  const loadMoreCompletedTasks = useCallback(async () => {
    if (completedHasMore && !isLoadingMoreCompleted) {
      setIsLoadingMoreCompleted(true);
      const nextPage = completedCurrentPage + 1;
      await fetchCompletedTasks(nextPage, true);
      setIsLoadingMoreCompleted(false);
    }
  }, [completedHasMore, completedCurrentPage, fetchCompletedTasks, isLoadingMoreCompleted]);

  // Load completed tasks when section is expanded or category changes
  useEffect(() => {
    if (showCompleted) {
      // Reset and fetch when category changes or when first expanded
      setCompletedTasksList([]);
      setCompletedTasksLoaded(false);
      setCompletedCurrentPage(1);
      setCompletedHasMore(true);
      fetchCompletedTasks(1, false);
      setCompletedTasksLoaded(true);
    }
  }, [showCompleted, selectedCategory, fetchCompletedTasks]);
  
  // Filter completed tasks for focus mode (client-side filtering for date)
  const filteredCompletedTasks = useMemo(() => {
    if (focusMode) {
      const { today, tomorrow } = todayDateInfo;
      return completedTasksList.filter(task => {
        if (!task.updatedAt) return false;
        const completionDate = new Date(task.updatedAt);
        const completionDateOnly = new Date(completionDate.getFullYear(), completionDate.getMonth(), completionDate.getDate());
        return completionDateOnly.getTime() >= today.getTime() && completionDateOnly.getTime() < tomorrow.getTime();
      });
    }
    return completedTasksList;
  }, [completedTasksList, focusMode, todayDateInfo]);
  
  const sortedCompletedTasks = useMemo(() => 
    [...filteredCompletedTasks].sort((a, b) => {
      return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
    }), 
    [filteredCompletedTasks]
  );

  // Initialize custom order when tasks change and we're not in custom mode
  useEffect(() => {
    if (sortBy !== 'custom' && sortedActiveTasks.length > 0) {
      setCustomOrder([]);
    }
  }, [sortedActiveTasks, sortBy]);

  // Reset custom order when tasks change (e.g., category filter)
  useEffect(() => {
    if (sortBy === 'custom' && customOrder.length > 0) {
      // Check if custom order is still valid with current tasks
      const currentTaskIds = activeTasks.map(task => task._id);
      const customOrderIds = customOrder.map(task => task._id);
      
      // If the task sets don't match, reset custom order
      if (currentTaskIds.length !== customOrderIds.length || 
          !currentTaskIds.every(id => customOrderIds.includes(id))) {
        setCustomOrder([]);
        setSortBy('dueDate'); // Reset to default sort
      }
    }
  }, [activeTasks, customOrder, sortBy]);

  // Track mouse position during drag (throttled for performance)
  useEffect(() => {
    let rafId = null;
    const handleMouseMove = (e) => {
      if (draggedIndex !== null) {
        // Use requestAnimationFrame to throttle updates for better performance
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(() => {
          setMousePosition({ x: e.clientX, y: e.clientY });
        });
      }
    };

    if (draggedIndex !== null) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    }
  }, [draggedIndex]);

  const handleCategoryAdded = useCallback((newCategory) => {
    setCategories(prev => [...prev, newCategory]);
  }, []);

  const handleCategoryUpdated = useCallback((updatedCategory) => {
    // Update the category in the local state
    setCategories(prev => prev.map(cat => 
      cat._id === updatedCategory._id ? updatedCategory : cat
    ));
    // Refresh tasks and counts to reflect the updated category
    refreshTasks();
  }, [refreshTasks]);

  const handleCategoryDeleted = useCallback((deletedCategoryId) => {
    // Remove the category from local state
    setCategories(prev => prev.filter(cat => cat._id !== deletedCategoryId));
    // If the deleted category was selected, clear the selection
    if (selectedCategory === deletedCategoryId) {
      setSelectedCategory(null);
    }
    // Refresh tasks and counts to reflect the deleted category
    refreshTasks();
  }, [selectedCategory, refreshTasks]);

  // Helper function to normalize dates for consistent handling
  const normalizeDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      // If it's a date string with time (from database), extract just the date part
      if (typeof dateString === 'string' && dateString.includes('T')) {
        return dateString.split('T')[0];
      }
      // If it's already just a date string, return as is
      if (typeof dateString === 'string') {
        return dateString;
      }
      // If it's a Date object, format it
      if (dateString instanceof Date) {
        return dateString.toISOString().split('T')[0];
      }
      return '';
    } catch (error) {
      console.error('Error normalizing date:', error);
      return '';
    }
  };

  // Helper function to check if a date is today or tomorrow (memoized)
  const isTodayOrTomorrow = useCallback((dateString) => {
    if (!dateString) return null;
    
    try {
      let date;
      
      if (typeof dateString === 'string') {
        // If it's a date string with time (from database), extract just the date part
        if (dateString.includes('T')) {
          const datePart = dateString.split('T')[0];
          date = new Date(datePart + 'T12:00:00');
        } else {
          date = new Date(dateString + 'T12:00:00');
        }
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        return null;
      }
      
      // Use memoized today/tomorrow dates
      const { today, tomorrow } = todayDateInfo;
      
      // Normalize the task date to just the date part (no time)
      const taskDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      if (taskDate.getTime() === today.getTime()) {
        return 'today';
      } else if (taskDate.getTime() === tomorrow.getTime()) {
        return 'tomorrow';
      }
      
      return null;
    } catch (error) {
      console.error('Error checking if date is today or tomorrow:', error);
      return null;
    }
  }, [todayDateInfo]);

  // Helper function to format date without timezone issues
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      let date;
      
      if (typeof dateString === 'string') {
        // If it's a date string with time (from database), extract just the date part
        if (dateString.includes('T')) {
          // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
          const datePart = dateString.split('T')[0];
          // Create date in local timezone by adding time
          date = new Date(datePart + 'T12:00:00');
        } else {
          // If it's already just a date string, create date in local timezone
          date = new Date(dateString + 'T12:00:00');
        }
      } else {
        // If it's already a Date object
        date = new Date(dateString);
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return '';
      }
      
      return format(date, 'MMMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Helper function to get display text for due date (memoized)
  const getDueDateDisplay = useCallback((dateString) => {
    const todayOrTomorrow = isTodayOrTomorrow(dateString);
    
    if (todayOrTomorrow === 'today') {
      return 'Due Today';
    } else if (todayOrTomorrow === 'tomorrow') {
      return 'Due Tomorrow';
    } else {
      return `Due: ${formatDate(dateString)}`;
    }
  }, [isTodayOrTomorrow]);

  // Helper function to check if a date is past due (memoized)
  const isPastDue = useCallback((dateString) => {
    if (!dateString) return false;
    
    try {
      let date;
      
      if (typeof dateString === 'string') {
        if (dateString.includes('T')) {
          const datePart = dateString.split('T')[0];
          date = new Date(datePart + 'T12:00:00');
        } else {
          date = new Date(dateString + 'T12:00:00');
        }
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        return false;
      }
      
      // Use memoized today date
      const { today } = todayDateInfo;
      
      // Normalize the task date to just the date part (no time)
      const taskDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      return taskDate.getTime() < today.getTime();
    } catch (error) {
      console.error('Error checking if date is past due:', error);
      return false;
    }
  }, [todayDateInfo]);

  // Helper function to get due date color (memoized)
  const getDueDateColor = useCallback((dateString) => {
    if (!dateString) return theme.palette.text.secondary;
    
    const todayOrTomorrow = isTodayOrTomorrow(dateString);
    if (todayOrTomorrow === 'today') {
      return theme.palette.info.main || theme.palette.primary.main; // Blue for today
    } else if (isPastDue(dateString)) {
      return theme.palette.error.main; // Red for past due
    }
    
    return theme.palette.text.secondary; // Default color
  }, [isTodayOrTomorrow, isPastDue, theme.palette]);

  // Use taskCounts from API
  const calculateTaskCounts = taskCounts;

  const handleSelectFocus = useCallback(() => {
    // Use requestAnimationFrame for instant UI update
    requestAnimationFrame(() => {
      setFocusMode(true);
      setSelectedCategory(null);
    });
  }, []);

  const handleSelectCategory = useCallback((catId) => {
    setFocusMode(false);
    // Use requestAnimationFrame for instant UI update
    requestAnimationFrame(() => {
      setSelectedCategory(catId);
    });
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  // Memoize context menu date calculations to avoid recalculation on every render
  const contextMenuDateInfo = useMemo(() => {
    if (!selectedTask || !selectedTask.dueDate) {
      return { isDueToday: false, isDueTomorrow: false };
    }
    const todayOrTomorrow = isTodayOrTomorrow(selectedTask.dueDate);
    return {
      isDueToday: todayOrTomorrow === 'today',
      isDueTomorrow: todayOrTomorrow === 'tomorrow',
    };
  }, [selectedTask, isTodayOrTomorrow]);

  // Context menu handlers - optimized with passive event listener
  const handleContextMenu = useCallback((event, task) => {
    event.preventDefault();
    event.stopPropagation();
    // Use requestAnimationFrame to defer state updates for better performance
    requestAnimationFrame(() => {
      setSelectedTask(task);
      setContextMenu({
        mouseX: event.clientX + 2,
        mouseY: event.clientY - 6,
      });
    });
  }, []);

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedTask(null);
  };

  const handleDueTodayToggle = async (targetDate, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!selectedTask) return;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const newDueDate = targetDate === 'today' ? today : tomorrow;
    const newDueDateString = newDueDate.toISOString().split('T')[0];
    
    // Optimistic update - update UI immediately
    const updatedTask = { ...selectedTask, dueDate: newDueDateString };
    setTasks(prevTasks => prevTasks.map(task => 
      task._id === selectedTask._id ? updatedTask : task
    ));
    setAllTasks(prevAllTasks => prevAllTasks.map(task => 
      task._id === selectedTask._id ? updatedTask : task
    ));
    
    // Close context menu immediately for better UX
    handleCloseContextMenu();
    
    // Perform API call in background (non-blocking)
    try {
      const token = localStorage.getItem('token');
      const response = await api.put(
        `/api/tasks/${selectedTask._id}`,
        { dueDate: newDueDateString },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update with server response to ensure consistency
      setTasks(prevTasks => prevTasks.map(task => 
        task._id === selectedTask._id ? response.data : task
      ));
      setAllTasks(prevAllTasks => prevAllTasks.map(task => 
        task._id === selectedTask._id ? response.data : task
      ));
    } catch (error) {
      console.error('Error updating due date:', error);
      // Revert optimistic update on error
      setTasks(prevTasks => prevTasks.map(task => 
        task._id === selectedTask._id ? selectedTask : task
      ));
      setAllTasks(prevAllTasks => prevAllTasks.map(task => 
        task._id === selectedTask._id ? selectedTask : task
      ));
      // Refresh to get correct state
      refreshTasks();
    }
  };



  const handleDatePickerSubmit = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!selectedTask || isUpdatingDate) return;
    
    setIsUpdatingDate(true);
    
    const newDueDate = quickDateValue || null;
    
    // Optimistic update - update UI immediately
    const updatedTask = { ...selectedTask, dueDate: newDueDate };
    setTasks(prevTasks => prevTasks.map(task => 
      task._id === selectedTask._id ? updatedTask : task
    ));
    setAllTasks(prevAllTasks => prevAllTasks.map(task => 
      task._id === selectedTask._id ? updatedTask : task
    ));
    
    // Close dialog immediately for better UX
    setDatePickerOpen(false);
    setSelectedTask(null);
    setQuickDateValue('');
    setIsUpdatingDate(false);
    
    // Perform API call in background (non-blocking)
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await api.put(
        `/api/tasks/${selectedTask._id}`,
        { dueDate: newDueDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update with server response to ensure consistency
      setTasks(prevTasks => prevTasks.map(task => 
        task._id === selectedTask._id ? response.data : task
      ));
      setAllTasks(prevAllTasks => prevAllTasks.map(task => 
        task._id === selectedTask._id ? response.data : task
      ));
    } catch (error) {
      console.error('Error updating due date:', error);
      // Revert optimistic update on error
      setTasks(prevTasks => prevTasks.map(task => 
        task._id === selectedTask._id ? selectedTask : task
      ));
      setAllTasks(prevAllTasks => prevAllTasks.map(task => 
        task._id === selectedTask._id ? selectedTask : task
      ));
      alert('Failed to update due date. Please try again.');
    }
  };


  // New handlers for improved UX
  const handleDirectDatePicker = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!selectedTask) return;
    setQuickDateValue(selectedTask.dueDate ? normalizeDate(selectedTask.dueDate) : '');
    setDatePickerOpen(true);
    // Don't clear selectedTask here - we need it for the update
    setContextMenu(null); // Only close the context menu
    
    // Auto-open the date picker after a short delay to ensure the dialog is rendered
    setTimeout(() => {
      const dateInput = document.getElementById('due-date-picker');
      if (dateInput && dateInput.showPicker) {
        try {
          dateInput.showPicker();
        } catch (error) {
          console.log('Could not auto-open date picker:', error);
          // This is expected in some browsers due to CSP restrictions
        }
      }
    }, 50);
  };

  const handleToggleFocus = useCallback(async (task) => {
    try {
      const newFocusStatus = !task.focus;
      
      // Use memoized today/tomorrow dates
      const { today, tomorrow } = todayDateInfo;
      
      // Check if task is due today - normalize dates to compare date-only
      let isDueToday = false;
      if (task.dueDate) {
        const taskDueDate = new Date(task.dueDate);
        const taskDateOnly = new Date(taskDueDate.getFullYear(), taskDueDate.getMonth(), taskDueDate.getDate());
        isDueToday = taskDateOnly.getTime() >= today.getTime() && taskDateOnly.getTime() < tomorrow.getTime();
      }
      
      // Determine the manuallyFocused and manuallyUnfocused flags
      let manuallyFocused = undefined;
      let manuallyUnfocused = undefined;
      
      if (newFocusStatus) {
        // User is focusing the task
        if (!isDueToday) {
          // User is focusing a task that's not due today
          manuallyFocused = true;
        }
        // If user focuses a task that was manually unfocused, clear the manuallyUnfocused flag
        if (task.manuallyUnfocused) {
          manuallyUnfocused = false;
        }
      } else {
        // User is unfocusing the task
        if (task.manuallyFocused) {
          // User is unfocusing a task that was manually focused
          manuallyFocused = false;
        }
        if (isDueToday) {
          // User is unfocusing a task due today - set the flag to prevent auto-focus
          manuallyUnfocused = true;
        }
      }
      
      const updateData = { 
        focus: newFocusStatus
      };
      
      // Only include flags if they need to be updated
      if (manuallyFocused !== undefined) {
        updateData.manuallyFocused = manuallyFocused;
      }
      if (manuallyUnfocused !== undefined) {
        updateData.manuallyUnfocused = manuallyUnfocused;
      }
      
      // Optimistically update local state immediately for instant UI feedback
      setTasks(prevTasks => prevTasks.map(t => {
        if (t._id === task._id) {
          return {
            ...t,
            focus: newFocusStatus,
            manuallyUnfocused: manuallyUnfocused !== undefined ? manuallyUnfocused : t.manuallyUnfocused,
            manuallyFocused: manuallyFocused !== undefined ? manuallyFocused : t.manuallyFocused
          };
        }
        return t;
      }));
      
      // Also update allTasks for counts
      setAllTasks(prevAllTasks => prevAllTasks.map(t => {
        if (t._id === task._id) {
          return {
            ...t,
            focus: newFocusStatus,
            manuallyUnfocused: manuallyUnfocused !== undefined ? manuallyUnfocused : t.manuallyUnfocused,
            manuallyFocused: manuallyFocused !== undefined ? manuallyFocused : t.manuallyFocused
          };
        }
        return t;
      }));
      
      // Then update on server (non-blocking after optimistic update)
      const token = localStorage.getItem('token');
      const response = await api.put(
        `/api/tasks/${task._id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update with server response to ensure consistency (no full refresh needed)
      setTasks(prevTasks => prevTasks.map(t => 
        t._id === task._id ? response.data : t
      ));
      setAllTasks(prevAllTasks => prevAllTasks.map(t => 
        t._id === task._id ? response.data : t
      ));
      
      // Refresh task counts to update focus count display
      fetchTaskCounts();
    } catch (error) {
      console.error('Error toggling focus:', error);
      // Revert optimistic update on error
      setTasks(prevTasks => prevTasks.map(t => 
        t._id === task._id ? task : t
      ));
      setAllTasks(prevAllTasks => prevAllTasks.map(t => 
        t._id === task._id ? task : t
      ));
      // Only refresh on error to get correct state
      refreshTasks();
    }
  }, [todayDateInfo, refreshTasks, fetchTaskCounts]);

  const handleDragStart = useCallback((e, index) => {
    const task = sortedActiveTasks[index];
    setDraggedIndex(index);
    setDraggedTask(task);
    setMousePosition({ x: e.clientX, y: e.clientY });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  }, [sortedActiveTasks]);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const elementCenter = rect.top + rect.height / 2;
    
    let position, targetIndex;
    
    // Simplified logic - just use the center point
    if (mouseY < elementCenter) {
      position = 'above';
      targetIndex = index;
    } else {
      position = 'below';
      targetIndex = index;
    }
    
    // Special case: if dragging above the first task, show top indicator
    if (index === 0 && position === 'above') {
      targetIndex = 0;
      position = 'above';
    }
    
    // Special case: if dragging below the last task, show bottom indicator  
    if (index === sortedActiveTasks.length - 1 && position === 'below') {
      targetIndex = sortedActiveTasks.length - 1;
      position = 'below';
    }
    
    setDropPosition(position);
    setShowDropGap({ index: targetIndex, position });
    setMousePosition({ x: e.clientX, y: e.clientY });
  }, [sortedActiveTasks.length]);

  const handleDragLeave = useCallback((e) => {
    setDropPosition(null);
    setShowDropGap(null);
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null) {
      setDraggedIndex(null);
      return;
    }

    // Calculate the actual drop position based on the current drop gap
    let actualDropIndex = dropIndex;
    
    if (showDropGap) {
      if (showDropGap.position === 'above') {
        // Line is above the task, insert at that task's position
        actualDropIndex = showDropGap.index;
      } else {
        // Line is below the task, insert after that task
        actualDropIndex = showDropGap.index + 1;
      }
    } else {
      // Fallback to the drop index passed to the function
      actualDropIndex = dropIndex;
    }
    
    // Don't drop on the same position
    if (draggedIndex === actualDropIndex) {
      setDraggedIndex(null);
      return;
    }

    // Create new order
    const newOrder = Array.from(sortedActiveTasks);
    const [movedTask] = newOrder.splice(draggedIndex, 1);
    
    // Adjust the drop index if we removed a task before the drop position
    if (draggedIndex < actualDropIndex) {
      actualDropIndex -= 1;
    }
    
    newOrder.splice(actualDropIndex, 0, movedTask);

    // Update state
    setCustomOrder(newOrder);
    setSortBy('custom');
    setDraggedIndex(null);
  }, [draggedIndex, showDropGap, sortedActiveTasks]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDropPosition(null);
    setShowDropGap(null);
    setDraggedTask(null);
    setMousePosition({ x: 0, y: 0 });
  }, []);

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <CategorySidebar
        selectedCategory={selectedCategory}
        onSelectCategory={handleSelectCategory}
        onCategoryAdded={handleCategoryAdded}
        onCategoryUpdated={handleCategoryUpdated}
        onCategoryDeleted={handleCategoryDeleted}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
        focusMode={focusMode}
        onSelectFocus={handleSelectFocus}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        taskCounts={calculateTaskCounts}
      />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          p: { xs: 3, sm: 4, md: 4.5 }, 
          pt: { xs: 3, sm: 3.5, md: 4 },
          borderBottom: 'none',
          ml: { xs: 0, sm: sidebarCollapsed ? '60px' : '240px' },
          transition: 'margin-left 0.3s ease',
          bgcolor: 'background.default',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ color: theme.palette.text.primary }}>Sort by</InputLabel>
              <Select
                value={sortBy}
                label="Sort by"
                onChange={(e) => setSortBy(e.target.value)}
                sx={{
                  borderRadius: 2,
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  '.MuiSelect-icon': { color: theme.palette.text.secondary },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      '& .MuiMenuItem-root': {
                        color: theme.palette.text.primary,
                      },
                      '& .MuiMenuItem-root.Mui-selected': {
                        bgcolor: theme.palette.action.selected,
                      },
                    },
                  },
                }}
              >
                <MenuItem value="dueDate">Due Date</MenuItem>
                <MenuItem value="priority">Priority</MenuItem>
                <MenuItem value="createdAt">Created Date</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            
              {selectedCategory && (
                <Chip
                  label={`Filtered by: ${getCategoryName(selectedCategory)}`}
                  onDelete={() => setSelectedCategory(null)}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ 
                borderRadius: '12px',
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.08)',
                transition: 'all 80ms ease-out',
                '&:hover': {
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Add Task
            </Button>
          </Box>
        </Box>
        <Box 
          sx={{ 
            flexGrow: 1, 
            overflow: 'hidden',
            ml: { xs: 0, sm: sidebarCollapsed ? '60px' : '240px' },
            transition: 'margin-left 0.3s ease',
            px: { xs: 2, sm: 4, md: 4.5 },
            py: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <QuickAddForm
            categories={categories}
            selectedCategory={selectedCategory}
            onQuickAdd={handleQuickAdd}
          />
          <List 
            sx={{ 
              minHeight: 50,
              flex: 1,
              overflow: 'auto'
            }}
            onScroll={(e) => {
              // Infinite scroll detection - load more when near bottom
              const target = e.currentTarget;
              const scrollTop = target.scrollTop;
              const scrollHeight = target.scrollHeight;
              const clientHeight = target.clientHeight;

              // Load more when within 300px of bottom (increased threshold to prevent rapid firing)
              const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
              if (distanceFromBottom < 300 && hasMore && !isLoadingMore && !isLoadingMoreRef.current) {
                loadMoreTasks();
              }
            }}
            onDragOver={(e) => {
              // Handle drag over the list container for top positioning
              if (draggedIndex !== null) {
                const rect = e.currentTarget.getBoundingClientRect();
                const mouseY = e.clientY;
                const firstTaskTop = rect.top + 20; // Account for padding
                
                if (mouseY < firstTaskTop) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDropPosition('above');
                  setShowDropGap({ index: 0, position: 'above' });
                }
              }
            }}
            onDrop={(e) => {
              if (showDropGap?.index === 0 && showDropGap?.position === 'above') {
                handleDrop(e, 0);
              }
            }}
          >
            {sortedActiveTasks.length > 0 ? (
              <>
                {/* Drop gap indicator - at the very top */}
                {showDropGap?.index === 0 && showDropGap?.position === 'above' && (
                  <Box
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => handleDrop(e, 0)}
                    sx={{
                      height: '16px',
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '8px',
                      mb: 3,
                      opacity: 0.6,
                      boxShadow: `0 0 8px ${theme.palette.primary.main}`,
                      cursor: 'pointer',
                    }}
                  />
                )}
                
                {/* Regular list rendering with pagination for performance */}
                {sortedActiveTasks.map((task, index) => (
                  <TaskListItem
                    key={task._id}
                    task={task}
                    index={index}
                    draggedIndex={draggedIndex}
                    showDropGap={showDropGap}
                    sortedActiveTasksLength={sortedActiveTasks.length}
                    completedTasks={completedTasks}
                    theme={theme}
                    getCategoryIcon={getCategoryIcon}
                    getCategoryName={getCategoryName}
                    getDueDateDisplay={getDueDateDisplay}
                    getDueDateColor={getDueDateColor}
                    handleToggleStatus={handleToggleStatus}
                    handleToggleFocus={handleToggleFocus}
                    handleDelete={handleDelete}
                    handleOpenDialog={handleOpenDialog}
                    handleDragStart={handleDragStart}
                    handleDragOver={handleDragOver}
                    handleDragLeave={handleDragLeave}
                    handleDrop={handleDrop}
                    handleDragEnd={handleDragEnd}
                    handleContextMenu={handleContextMenu}
                  />
                ))}
                
                {/* Loading indicator for infinite scroll */}
                {isLoadingMore && (
                  <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading more tasks...
                    </Typography>
                  </ListItem>
                )}
                
                {/* Drop gap indicator - at the very bottom */}
                {showDropGap?.index === sortedActiveTasks.length - 1 && showDropGap?.position === 'below' && (
                  <Box
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => handleDrop(e, sortedActiveTasks.length)}
                    sx={{
                      height: '16px',
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '8px',
                      mt: 3,
                      opacity: 0.6,
                      boxShadow: `0 0 8px ${theme.palette.primary.main}`,
                      cursor: 'pointer',
                    }}
                  />
                )}
              </>
            ) : (
              <ListItem sx={{ justifyContent: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No active tasks
                </Typography>
              </ListItem>
            )}
          </List>

          {/* Floating dragged task that follows cursor */}
          {draggedTask && (
            <Box
              sx={{
                position: 'fixed',
                left: mousePosition.x - 150,
                top: mousePosition.y - 25,
                zIndex: 9999,
                pointerEvents: 'none',
                transform: 'rotate(2deg)',
                opacity: 0.9,
                maxWidth: '300px',
                minWidth: '200px',
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 6,
                  border: `2px solid ${theme.palette.primary.main}`,
                  bgcolor: 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    type="button"
                    size="small"
                    color={draggedTask.status === 'completed' ? 'success' : 'default'}
                    sx={{ mt: 0.5 }}
                  >
                    {draggedTask.status === 'completed' ? <CheckCircleIcon /> : <UncheckedIcon />}
                  </IconButton>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        textDecoration: draggedTask.status === 'completed' ? 'line-through' : 'none',
                        color: draggedTask.status === 'completed' ? 'text.secondary' : 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {draggedTask.title}
                    </Typography>
                    {draggedTask.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {draggedTask.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Completed Tasks Section */}
          {((focusMode && calculateTaskCounts.focus.completed > 0) || (!focusMode && calculateTaskCounts.all.completed > 0)) && (
            <Box sx={{ mt: 3 }}>
              <ListItem
                button
                onClick={() => setShowCompleted(!showCompleted)}
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  mb: 2,
                  px: 3,
                  py: 1.5,
                }}
              >
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      Completed ({(() => {
                        if (focusMode) {
                          return calculateTaskCounts.focus.completed;
                        } else if (selectedCategory && calculateTaskCounts.categories[selectedCategory]) {
                          return calculateTaskCounts.categories[selectedCategory].completed;
                        } else {
                          return calculateTaskCounts.all.completed;
                        }
                      })()})
                    </Typography>
                  }
                />
                <IconButton type="button" size="small" onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowCompleted(!showCompleted);
                }}>
                  {showCompleted ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </ListItem>
              
              <Collapse in={showCompleted}>
                <List sx={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {showCompleted && !completedTasksLoaded ? (
                    <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Loading completed tasks...
                      </Typography>
                    </ListItem>
                  ) : sortedCompletedTasks.length > 0 ? sortedCompletedTasks.map((task, index) => (
                    <ListItem
                      key={task._id}
                      onContextMenu={(e) => handleContextMenu(e, task)}
                      onClick={() => handleOpenDialog(task)}
                      sx={{
                        bgcolor: 'background.paper',
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: 1,
                        border: '1px solid',
                        borderColor: 'grey.100',
                        px: 3,
                        py: 2,
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          boxShadow: 6,
                          background:
                            theme.palette.mode === 'dark'
                              ? '#23272e'
                              : theme.palette.taskHover || 'grey.100',
                        '& .delete-button': {
                          opacity: 1,
                        },
                        },
                        '& .delete-button': {
                          opacity: 0,
                          pointerEvents: 'none',
                          transition: 'opacity 150ms ease-out',
                        },
                        '&:hover .delete-button': {
                          opacity: 1,
                          pointerEvents: 'auto',
                          transition: 'opacity 150ms ease-out',
                        },
                        alignItems: 'center',
                        cursor: 'pointer',
                        display: 'flex',
                        gap: 2,
                        opacity: 0.7,
                      }}
                    >
                      <IconButton
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleToggleStatus(task._id, task.status, e);
                        }}
                        color="success"
                        sx={{ mt: 0.5 }}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                      <IconButton
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleToggleFocus(task);
                        }}
                        sx={{
                          color: task.focus ? 'warning.main' : 'grey.400',
                          ml: 1,
                          transition: 'color 0.2s',
                          '&:hover': { color: 'warning.dark', bgcolor: 'rgba(255,193,7,0.08)' },
                        }}
                      >
                        {task.focus ? <StarIcon /> : <StarBorderIcon />}
                      </IconButton>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 500,
                            fontSize: { xs: 15, sm: 17 },
                            textDecoration: 'line-through',
                            color: theme.palette.text.primary,
                            wordBreak: 'break-word',
                          }}
                        >
                          {task.title}
                        </Typography>
                        {task.description && (
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 0.5,
                              wordBreak: 'break-word',
                              fontSize: { xs: 13, sm: 14 },
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {task.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                          {task.dueDate && (
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: 12 }}>
                              {getDueDateDisplay(task.dueDate)}
                            </Typography>
                          )}
                          {task.recurring?.enabled && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <RepeatIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                              <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontSize: 12 }}>
                                {task.recurring.frequency === 'custom' 
                                  ? `Every ${task.recurring.customValue} ${task.recurring.customUnit}`
                                  : task.recurring.frequency === 'weekly' && task.recurring.weeklyDays && task.recurring.weeklyDays.length > 0
                                  ? `Weekly (${task.recurring.weeklyDays.map(day => day.charAt(0).toUpperCase() + day.slice(1, 3)).join(', ')})`
                                  : task.recurring.frequency.charAt(0).toUpperCase() + task.recurring.frequency.slice(1)
                                }
                              </Typography>
                            </Box>
                          )}
                          {task.category && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {(() => {
                                const IconComponent = getCategoryIcon(task.category);
                                return IconComponent ? React.createElement(IconComponent, { fontSize: 'small' }) : null;
                              })()}
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: 12 }}>
                                {getCategoryName(task.category)}
                              </Typography>
                            </Box>
                          )}
                          <Chip
                            label={task.priority}
                            size="small"
                            sx={{ 
                              height: 22,
                              fontSize: '11px',
                              fontWeight: 500,
                              borderRadius: '999px',
                              px: 1.5,
                              ...(task.priority === 'high' && {
                                bgcolor: '#F8D4D4',
                                color: '#8B2E2E',
                              }),
                              ...(task.priority === 'medium' && {
                                bgcolor: '#FCEED4',
                                color: '#8B5A2B',
                              }),
                              ...(task.priority === 'low' && {
                                bgcolor: '#E4F3FD',
                                color: '#1E4A72',
                              }),
                              '& .MuiChip-label': {
                                px: 1.5,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                      <IconButton
                        type="button"
                        edge="end"
                        className="delete-button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDelete(task._id);
                        }}
                        sx={{
                          color: 'grey.500',
                          ml: 1,
                          transition: 'color 0.2s',
                          '&:hover': { color: 'error.main', bgcolor: 'rgba(244,67,54,0.08)' },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  )) : (
                    <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No completed tasks
                      </Typography>
                    </ListItem>
                  )}
                  {completedHasMore && sortedCompletedTasks.length > 0 && (
                    <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                      <Button 
                        onClick={loadMoreCompletedTasks} 
                        disabled={isLoadingMoreCompleted}
                        variant="outlined"
                        size="small"
                      >
                        {isLoadingMoreCompleted ? 'Loading...' : 'Load More Completed Tasks'}
                      </Button>
                    </ListItem>
                  )}
                </List>
              </Collapse>
            </Box>
          )}
        </Box>

        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          disableEnforceFocus
          disableAutoFocus
          TransitionProps={{ timeout: 200 }}
        >
          <DialogTitle>{editingTask ? 'Edit Task' : 'Create Task'}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                margin="normal"
                multiline
                rows={3}
              />
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category || ''}
                  onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  label="Category"
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Divider sx={{ my: 2 }} />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.recurring.enabled}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      recurring: { ...prev.recurring, enabled: e.target.checked }
                    }))}
                  />
                }
                label="Make this task recurring"
              />
              
              {formData.recurring.enabled && (
                <Box sx={{ mt: 2 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Repeat Frequency</InputLabel>
                    <Select
                      value={formData.recurring.frequency}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        recurring: { ...prev.recurring, frequency: e.target.value }
                      }))}
                      label="Repeat Frequency"
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="yearly">Yearly</MenuItem>
                      <MenuItem value="custom">Custom</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {formData.recurring.frequency === 'weekly' && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.primary }}>
                        Select days of the week:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {[
                          { value: 'monday', label: 'Mon' },
                          { value: 'tuesday', label: 'Tue' },
                          { value: 'wednesday', label: 'Wed' },
                          { value: 'thursday', label: 'Thu' },
                          { value: 'friday', label: 'Fri' },
                          { value: 'saturday', label: 'Sat' },
                          { value: 'sunday', label: 'Sun' }
                        ].map((day) => {
                          const isSelected = formData.recurring.weeklyDays?.includes(day.value);
                          return (
                            <Chip
                              key={day.value}
                              label={day.label}
                              clickable
                              color={isSelected ? 'primary' : 'default'}
                              variant={isSelected ? 'filled' : 'outlined'}
                              onClick={() => {
                                setFormData(prev => {
                                  const currentDays = prev.recurring.weeklyDays || [];
                                  const newDays = currentDays.includes(day.value)
                                    ? currentDays.filter(d => d !== day.value)
                                    : [...currentDays, day.value];
                                  return {
                                    ...prev,
                                    recurring: { ...prev.recurring, weeklyDays: newDays }
                                  };
                                });
                              }}
                              sx={{
                                minWidth: 50,
                                '&:hover': {
                                  backgroundColor: isSelected 
                                    ? theme.palette.primary.dark 
                                    : theme.palette.action.hover
                                }
                              }}
                            />
                          );
                        })}
                      </Box>
                      {(!formData.recurring.weeklyDays || formData.recurring.weeklyDays.length === 0) && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                          Please select at least one day for weekly recurrence
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {formData.recurring.frequency === 'custom' && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Value"
                          type="number"
                          value={formData.recurring.customValue}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            recurring: { ...prev.recurring, customValue: parseInt(e.target.value) || 1 }
                          }))}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <InputLabel>Unit</InputLabel>
                          <Select
                            value={formData.recurring.customUnit}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              recurring: { ...prev.recurring, customUnit: e.target.value }
                            }))}
                            label="Unit"
                          >
                            <MenuItem value="days">Days</MenuItem>
                            <MenuItem value="weeks">Weeks</MenuItem>
                            <MenuItem value="months">Months</MenuItem>
                            <MenuItem value="years">Years</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button type="button" onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained" color="primary">
                {editingTask ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
      
      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 200,
            boxShadow: 3,
          }
        }}
      >
        {selectedTask && (
          <>
            {!contextMenuDateInfo.isDueToday && (
              <MenuItem 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDueTodayToggle('today', e);
                }} 
                sx={{ py: 1.5 }}
              >
                <ListItemIcon>
                  <CalendarTodayIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Due Today</ListItemText>
              </MenuItem>
            )}
            
            {!contextMenuDateInfo.isDueTomorrow && (
              <MenuItem 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDueTodayToggle('tomorrow', e);
                }} 
                sx={{ py: 1.5 }}
              >
                <ListItemIcon>
                  <EventIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Due Tomorrow</ListItemText>
              </MenuItem>
            )}
          </>
        )}
        
        <MenuItem 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDirectDatePicker(e);
          }} 
          sx={{ py: 1.5 }}
        >
          <ListItemIcon>
            <CalendarMonthIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Pick a Date</ListItemText>
        </MenuItem>
        
      </Menu>


      {/* Quick Date Picker Dialog */}
      <Dialog 
        open={datePickerOpen} 
        onClose={() => {
          setDatePickerOpen(false);
          setSelectedTask(null);
          setQuickDateValue('');
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Set Due Date</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            id="due-date-picker"
            name="dueDate"
            label="Due Date"
            type="date"
            value={quickDateValue}
            onChange={(e) => setQuickDateValue(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            type="button"
            onClick={() => {
              setDatePickerOpen(false);
              setSelectedTask(null);
              setQuickDateValue('');
            }} 
            disabled={isUpdatingDate}
          >
            Cancel
          </Button>
          <Button 
            type="button"
            onClick={handleDatePickerSubmit} 
            variant="contained" 
            color="primary"
            disabled={isUpdatingDate}
          >
            {isUpdatingDate ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default TaskList;
